import React, { useState, useMemo } from 'react';
import _ from 'lodash';
import moment from 'moment';
import ReactTooltip from 'react-tooltip';

import { CAMPAIGN_WINDOWS, CAMPAIGN_TYPE } from 'utils/campaign';
import { CSV_MODAL_TYPES, saveToCSV } from 'utils/admin-csv';
import env from 'utils/env';

import Button, { BUTTON_TYPES } from 'shared-components/Button/Button';
import { showSuccessToast } from 'shared-components/BaseToast/Toast';
import { AdminSelect, AdminFormRow } from 'shared-components';
import AdminSearchBar from 'shared-components/AdminSearchBar/AdminSearchBar';
import CSVModal from 'shared-components/CSVModal/CSVModal';
import AddProductsToCampaignModal from 'shared-components/CSVModal/AddProductsToCampaignModal';
import searchStyles from 'shared-components/AdminSearchBar/AdminSearchBar.module.css';
import campaignModel from '../campaignModel';
import CampaignModal from './CampaignModal';
import CampaignTable from '../CampaignTable/CampaignTable';
import DefaultCampaigns from './DefaultCampaigns/DefaultCampaigns';
import { categoryNamesFromIds } from '../utils';
import FlushCacheModal from '../FlushCacheModal/FlushCacheModal';
import CampaignForm from '../CampaignForm/CampaignForm';

import styles from './CampaignManagement.module.css';

const formatVariantDescriptors = (descriptors) =>
  _.map(descriptors, (item) => item.descriptor).join();

const getProductPreviewUrl = (campaignType, shareUrl, campaignId) => {
  if (!shareUrl) return null;
  const campaign =
    campaignType === CAMPAIGN_TYPE.season
      ? 'add-ons'
      : campaignType.toLowerCase();
  return `https://${env.hostname}/${campaign}/?product_link=${shareUrl}&campaign_id=${campaignId}`;
};

const formatProducts = (products, campaign = {}) => {
  const { id, type } = campaign;
  return _.map(products, (product) => ({
    ...product,
    variantDescriptorsDisplay: formatVariantDescriptors(
      product.variantDescriptors
    ),
    shippingRestrictionsDisplay: product.shippingRestrictions.join(', '),
    isDeleted: product.isDeleted.toString(),
    isSponsored: product.isSponsored.toString(),
    isTaxExempt: product.isTaxExempt.toString(),
    isShippingExempt: product.isShippingExempt.toString(),
    isSubscribable: product.isSubscribable.toString(),
    isPublicRefill: product.isPublicRefill.toString(),
    gwpThreshold: product.gwpThreshold.toFixed(2),
    retailPrice: product.retailPrice.toFixed(2),
    salePrice: product.salePrice.toFixed(2),
    productPreviewUrl: getProductPreviewUrl(type, product.shareUrl, id),
  }));
};

const filterProducts = (products, filter) => {
  const values = _.split(filter, ',');

  return _.filter(products, (product) => {
    return _.find(values, (value) => {
      const query = value.toLowerCase();
      return (
        _.includes(
          product.productName && product.productName.toLowerCase(),
          query
        ) ||
        _.includes(
          product.legacyPostId && product.legacyPostId.toString(),
          query
        ) ||
        _.includes(product.sku && product.sku.toLowerCase(), query)
      );
    });
  });
};

const processProducts = (products, filter) => {
  if (products.length) {
    const filteredProducts = filter
      ? filterProducts(products, filter)
      : products;
    return filteredProducts;
  }
  return products;
};

const processList = (row, key, categories) => {
  if (!row[key] || row[key] === '') {
    return '';
  }
  switch (key) {
    case 'categoryIds':
      return `"${categoryNamesFromIds(row[key], categories)}"`;
    default:
      return `"${row[key].join(',')}"`;
  }
};

const downloadCSV = (products, categories, filename) => {
  const colTitles = _.map(campaignModel, (col) => {
    if (col.key === 'variantDescriptorsDisplay') {
      return 'variantDescriptors';
    }
    if (col.key === 'shippingRestrictionsDisplay') {
      return 'shippingRestrictions';
    }
    if (col.key === 'categoryIds') {
      return 'categoryNames';
    }
    return col.key;
  });
  const processListType = (row, key) => processList(row, key, categories);
  saveToCSV(campaignModel, products, filename, colTitles, processListType);
};

const formatDate = (date) => moment(date).format('MM/DD/YYYY HH:mm:ss');

const getCampaignWindows = (type, premiumEndDate, endDate) => {
  if (type === 'SHOP') return CAMPAIGN_WINDOWS.zero;
  if (premiumEndDate === endDate) return CAMPAIGN_WINDOWS.one;
  return CAMPAIGN_WINDOWS.two;
};

const formatCampaignData = (campaignData) => {
  const {
    startDate,
    endDate,
    premiumStartDate,
    premiumEndDate,
    type,
    sponsoredThreshold,
  } = campaignData;

  return {
    ...campaignData,
    startDate: formatDate(startDate),
    endDate: formatDate(endDate),
    premiumStartDate: formatDate(premiumStartDate),
    premiumEndDate: formatDate(premiumEndDate),
    sponsoredThreshold: (sponsoredThreshold / 100).toString(),
    campaignWindows: getCampaignWindows(type, premiumEndDate, endDate),
  };
};

const CampaignManagement = ({
  campaigns,
  products,
  fetchingProducts,
  getProducts,
  categories,
  setCreateCampaignOpen,
  disableAdminEdit,
}) => {
  const [selectedCampaign, setSelectedCampaign] = useState('');
  const [modalOpen, setModalOpen] = useState(null);
  const [isFlushCacheModalOpen, setFlushCacheModal] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [formDisabled, setFormDisabled] = useState(true);
  const [selectedCampaignData, setSelectedCampaignData] = useState();
  const [editingForm, setEditingForm] = useState(false);
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const processedProducts = useMemo(
    () =>
      formatProducts(
        processProducts(products, searchQuery),
        selectedCampaignData
      ),
    [products, searchQuery]
  );
  const productsLoaded = _.size(processedProducts) > 0;
  const filename = productsLoaded ? `campaign-${selectedCampaign}` : null;

  const handleChangeCampaign = (event) => {
    setFormDisabled(true);
    setSelectedCampaign(event.target.value);
    getProducts(event.target.value);
    const campaignData = campaigns.filter(
      (c) => c.id.toString() === event.target.value
    )[0];
    if (campaignData) {
      setSelectedCampaignData(formatCampaignData(campaignData));
    }
  };

  const handleCancelEdit = () => {
    //trigger form to repopulate
    setSelectedCampaignData((prevState) => {
      return { ...prevState };
    });
    setFormDisabled(true);
  };

  const handleClickRefresh = () => {
    setSearchQuery('');
    getProducts(selectedCampaign);
  };
  const closeModal = () => {
    setModalOpen(null);
  };
  const refetchCampaign = () => {
    getProducts(selectedCampaign);
  };
  const handleUploadSuccess = ({ title }) => showSuccessToast(title);

  const handleNewCampaignButton = () => {
    if (editingForm) {
      setConfirmModalOpen(true);
    } else {
      setCreateCampaignOpen(true);
    }
  };

  return (
    <>
      <>
        <div className={styles.container}>
          <h1>Product Campaign Management</h1>
          <Button
            type="button"
            onClick={handleNewCampaignButton}
            buttonType={BUTTON_TYPES.TERTIARY}
          >
            Create A Campaign
          </Button>
        </div>
        <div>
          <DefaultCampaigns campaigns={campaigns} />
          <h4>Individual Campaign Details</h4>
          <AdminFormRow>
            <AdminSelect
              name="campaign"
              label="Selected Campaign"
              list={campaigns}
              selected={selectedCampaign}
              selectLabel="Select a Campaign"
              handleChange={handleChangeCampaign}
            />
            {selectedCampaign && formDisabled && (
              <Button
                type="button"
                buttonType={BUTTON_TYPES.PRIMARY_INVERSE}
                onClick={() => setFormDisabled(false)}
                style={{
                  alignSelf: 'flex-end',
                  width: '100px',
                }}
              >
                Edit
              </Button>
            )}
          </AdminFormRow>
          {selectedCampaign && (
            <CampaignForm
              formType="edit"
              formDisabled={formDisabled}
              campaignData={selectedCampaignData}
              onClose={handleCancelEdit}
              disableForm={() => setFormDisabled(true)}
              setEditingForm={setEditingForm}
              setSelectedCampaignData={setSelectedCampaignData}
              selectedCampaign={selectedCampaign}
            />
          )}
        </div>
        <div className={styles.products}>
          <div className={styles.leftPanel}>
            <h2>Products</h2>
            <AdminSearchBar
              query={searchQuery}
              setQuery={setSearchQuery}
              disabled={!selectedCampaign || fetchingProducts}
              styles={searchStyles}
              placeholder="Search by product name, sku, or post_id"
            />
          </div>
          <div className={styles.buttons}>
            <Button
              buttonType={BUTTON_TYPES.PRIMARY}
              style={{ width: '184px' }}
              disabled={
                disableAdminEdit || !selectedCampaign || fetchingProducts
              }
              onClick={() => setModalOpen(CSV_MODAL_TYPES.editCampaign)}
            >
              Upload CSV
            </Button>
            <button
              type="button"
              className="campaign-button outline"
              disabled={!selectedCampaign || fetchingProducts}
              onClick={handleClickRefresh}
              data-tip="Refresh"
            >
              <i className="far fa-sync" />
            </button>
            <button
              type="button"
              className="campaign-button outline"
              disabled={!productsLoaded || fetchingProducts}
              onClick={() =>
                downloadCSV(processedProducts, categories, filename)
              }
              data-tip="Download as CSV"
            >
              <i className="far fa-arrow-to-bottom" />
            </button>
            <button
              type="button"
              className="campaign-button outline"
              disabled={!selectedCampaign || fetchingProducts}
              onClick={() => setModalOpen(CSV_MODAL_TYPES.addToCampaign)}
              data-tip="Add Products"
            >
              <i className="far fa-plus" />
            </button>
            <button
              type="button"
              className="campaign-button outline"
              disabled={!selectedCampaign || fetchingProducts}
              onClick={() => setFlushCacheModal(true)}
              data-tip="Flush Cache"
            >
              <i className="far fa-recycle" />
            </button>
          </div>
        </div>
        <CampaignTable
          products={processedProducts}
          categories={categories}
          isLoading={fetchingProducts}
          campaignId={selectedCampaign}
          searchQuery={searchQuery}
          disableAdminEdit={disableAdminEdit}
        />
      </>
      <CampaignModal
        confirmModalOpen={confirmModalOpen}
        setConfirmModalOpen={setConfirmModalOpen}
        setCreateCampaignOpen={setCreateCampaignOpen}
      />
      {modalOpen === CSV_MODAL_TYPES.addToCampaign ? (
        <AddProductsToCampaignModal
          type={modalOpen}
          campaign={selectedCampaignData}
          onClickClose={closeModal}
          updateData={refetchCampaign}
          onUploadSuccess={handleUploadSuccess}
        />
      ) : (
        <CSVModal
          type={modalOpen}
          title="CSV Upload"
          campaign={selectedCampaignData}
          onClickClose={closeModal}
          updateData={refetchCampaign}
          onUploadSuccess={handleUploadSuccess}
        />
      )}
      <FlushCacheModal
        campaignId={parseInt(selectedCampaign, 10)}
        isOpen={isFlushCacheModalOpen}
        onRequestClose={() => setFlushCacheModal(false)}
      />
      <ReactTooltip
        className={styles.tooltip}
        place="top"
        type="dark"
        effect="solid"
      />
    </>
  );
};

export default CampaignManagement;
